import PropTypes from 'prop-types';
import { css, cx } from '@eti/styles';
import uniqBy from 'lodash/uniqBy';
import { TripCharacteristics } from '@eti/types';
import { useProperty, useSearch } from '@eti/providers';
import {
  getBoundType,
  getChangeOfAirportParameters,
  getEventSegmentsList,
  getOvernightStayCities,
} from '../utils/format';
import { bounds as boundsPropTypes } from '../propTypes';
import SelfTransferGuarantee from '../../../common/components/SelfTransferGuarantee';
import { ONE_WAY_COMBINATION } from '../../../constants/tripCharacteristics';
import { isTripWithDifferentOriginOrDestination } from '../utils/originDestinationUtil';
import TripDetailsBound from './TripDetailsBound';
import TripDetailsEventSegmentInfoList from './TripDetailsSegmentEventInfoList';
import TripDetailsInfoAccordion from './TripDetailsInfoAccordion';
import TripDetailsDifferentAirport from './TripDetailsDifferentAirport';

const boundsStyles = css`
  background-color: inherit;
  position: relative;
`;

export const Bounds = ({ children, className = '', ...rest }) => (
  <div className={cx(boundsStyles, className)} {...rest}>
    {children}
  </div>
);

const TripDetailsContent = ({
  bounds = [],
  children,
  className,
  eventSegmentListStyles,
  flightId,
  isExpanded,
  isMultiStop,
  isRyanAirTicketingOrMarketingCarrierPresent,
  isVI,
  onClick,
  shouldShowBoundHeader,
  tripCharacteristics = [],
}) => {
  const { p } = useProperty();
  const uniqueEventSegmentTypes = uniqBy(getEventSegmentsList(bounds).flat(), 'type');
  const overnightStayCities = getOvernightStayCities(bounds);
  const uniqueEventSegmentTypesWithLegendNumber = uniqueEventSegmentTypes.filter(
    ({ legendNumber }) => legendNumber && legendNumber > 0,
  );
  const overnightStayParameter = overnightStayCities.join(', ');
  const changeOfAirportParameters = getChangeOfAirportParameters(bounds);
  const { currentSearchData: search } = useSearch();

  const tripHasDifferentOriginOrDestination = isTripWithDifferentOriginOrDestination(
    search?.routes,
    bounds,
  );

  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');

  return (
    <>
      <Bounds
        className={className}
        data-testid="tripDetails-bounds"
        onClick={onClick}
        onKeyDown={onClick}
        role="button"
        tabIndex="0"
      >
        {bounds.map((bound, index) => (
          <TripDetailsBound
            {...bound}
            boundNumber={index + 1}
            flightId={flightId}
            isExpanded={isExpanded}
            isMultiStop={isMultiStop}
            key={`${flightId}-${bound.boundId}`}
            shouldShowBoundHeader={shouldShowBoundHeader}
            type={getBoundType(index)}
          />
        ))}
        {isVI && !isExpanded && <SelfTransferGuarantee isExpanded={false} />}
        {tripHasDifferentOriginOrDestination && !isExpanded && (
          <TripDetailsDifferentAirport isExpanded={false} />
        )}
        {children}
      </Bounds>
      {tripCharacteristics.includes(ONE_WAY_COMBINATION) && (
        <TripDetailsInfoAccordion flightId={flightId} infoType={ONE_WAY_COMBINATION} />
      )}
      {isCFCEnabled && isRyanAirTicketingOrMarketingCarrierPresent && (
        <TripDetailsInfoAccordion flightId={flightId} />
      )}
      {isExpanded && uniqueEventSegmentTypesWithLegendNumber.length > 0 && (
        <TripDetailsEventSegmentInfoList
          bounds={bounds}
          changeOfAirportParameters={changeOfAirportParameters}
          className={eventSegmentListStyles}
          eventSegmentTypes={uniqueEventSegmentTypesWithLegendNumber}
          overnightStayParameter={overnightStayParameter}
        />
      )}
    </>
  );
};

Bounds.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

TripDetailsContent.propTypes = {
  bounds: boundsPropTypes,
  children: PropTypes.node,
  className: PropTypes.string,
  eventSegmentListStyles: PropTypes.string,
  flightId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isExpanded: PropTypes.bool,
  isMultiStop: PropTypes.bool,
  isRyanAirTicketingOrMarketingCarrierPresent: PropTypes.bool,
  isVI: PropTypes.bool,
  onClick: PropTypes.func,
  shouldShowBoundHeader: PropTypes.bool,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
};

export default TripDetailsContent;
